import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import PageLayout from "../common/PageLayout";
import {
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  TableFooter,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { useTheme } from "@emotion/react";
import { PiUsersThreeThin } from "react-icons/pi";
import { UserRepository } from "../repositories/UserRepository";
import { CiEdit } from "react-icons/ci";
import { IoMdPersonAdd } from "react-icons/io";
import { resetRefresh } from "../../store/GlobalSlice";
import { GoDotFill } from "react-icons/go";
import { notifyShowErrorMessage } from "../../store/AlertSlice";
import { ErrorMessageResolver } from "../common/ErrorMessageResolver";
import { themeConfig } from "../../config/ThemeConfig";
import { TbFilterOff } from "react-icons/tb";

export default function Users() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [filterData, setFilterData] = useState({});
  const [roles, setRoles] = useState([]);
  const auth = useSelector((state) => state.auth);
  const [orderBy, setOrderBy] = useState("id");
  const [orderDirection, setOrderDirection] = useState("DESC");
  const shouldRefresh = useSelector(
    (state) => state.global.refreshFlags["users"]
  );

  useEffect(() => {
    if (shouldRefresh) {
      getAll();
      dispatch(resetRefresh("users"));
    }
  }, [shouldRefresh]);

  useEffect(() => {
    getAll();
  }, [page, size, filterData]);

  useEffect(() => {
    getAllRoles();
  }, []);

  const getAll = () => {
    setLoading(true);
    setData([]);
    UserRepository.getAll(page, size, filterData)
      .then((res) => {
        setLoading(false);
        setData(res.data);
      })
      .catch((err) => {
        setLoading(false);
        setData([]);
        console.error(err);
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
      });
  };

  const getAllRoles = () => {
    UserRepository.getAllRoles()
      .then((res) => {
        setRoles(res.data);
      })
      .catch((err) => {
        console.error("Error fetching roles:", err);
      });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setSize(newSize);
    setPage(0);
  };

  useEffect(() => {
    console.log(data);
  }, [data]);

  return (
    <PageLayout
      title="Корисници"
      Icon={PiUsersThreeThin}
      prev={location.pathname}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={2}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Корисничко име"
            label="Корисничко име"
            fullWidth
            value={filterData.username || ""}
            onChange={(e) =>
              setFilterData({ ...filterData, username: e.target.value })
            }
          />
        </Grid>

        <Grid item xs={12} md={2}>
          {auth?.userData?.roles?.includes("ROLE_SUPER_ADMIN") && (
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="role-label">Улога</InputLabel>
              <Select
                labelId="role-label"
                id="role"
                value={filterData.role || ""}
                onChange={(e) =>
                  setFilterData({ ...filterData, role: e.target.value })
                }
                label="Улога"
              >
                {roles?.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Grid>
        <Grid item xs={12} md={2}>
          {auth?.userData?.roles?.includes("ROLE_SUPER_ADMIN") && (
            <Button
              variant="transparent"
              sx={{
                padding: "0 10px",
                height: 38.56,
                border: "1px solid #d0d0d0",
              }}
              startIcon={<TbFilterOff style={{ color: "#ef233c" }} />}
              disabled={!filterData.username && !filterData.role}
              onClick={() => {
                setFilterData({});
              }}
              fullWidth
            >
              {!loading ? (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "14px",
                    fontFamily: "Inter-Regular",
                    marginLeft: "2px",
                    color: themeConfig.palette.secondary.main,
                  }}
                >
                  Исчисти
                </Typography>
              ) : (
                <CircularProgress size={20} id="circular-progress" />
              )}
            </Button>
          )}
        </Grid>
        <Grid item xs={0} md={3.5}></Grid>
        <Grid item xs={12} md={2.5}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            color="primary"
            endIcon={<IoMdPersonAdd fontSize={20} color="#fff" />}
            onClick={() => navigate("/users/new")}
          >
            <Typography
              style={{
                fontFamily: "Inter-Regular",
                fontSize: "16px",
                color: "#fff",
              }}
            >
              Нов корисник
            </Typography>
          </Button>
        </Grid>
        <Grid item xs={12}>
          <TableContainer>
            <Table size="small" style={{ marginTop: "20px" }}>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>
                    <Typography
                      variant="subtitle1"
                      style={{
                        fontFamily: "Inter-Medium",
                        fontSize: "13px",
                        lineHeight: "1.1",
                      }}
                    >
                      Корисничко име
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="subtitle1"
                      style={{
                        fontFamily: "Inter-Medium",
                        fontSize: "12px",
                        lineHeight: "1.1",
                        color: "#333",
                      }}
                    >
                      Име
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="subtitle1"
                      style={{
                        fontFamily: "Inter-Medium",
                        fontSize: "12px",
                        lineHeight: "1.1",
                        color: "#333",
                      }}
                    >
                      Презиме
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="subtitle1"
                      style={{
                        fontFamily: "Inter-Medium",
                        fontSize: "12px",
                        lineHeight: "1.1",
                        color: "#333",
                      }}
                    >
                      Статус
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="subtitle1"
                      style={{
                        fontFamily: "Inter-Medium",
                        fontSize: "12px",
                        lineHeight: "1.1",
                        color: "#333",
                      }}
                    >
                      Датум на креирање
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              {loading ? (
                <TableBody>
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      style={{ padding: "40px" }}
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {!loading &&
                    data?.content?.map((item, index) => (
                      <TableRow
                        key={index}
                        style={{
                          backgroundColor: index % 2 === 0 ? "#fafafa" : "#fff",
                        }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography
                            style={{
                              fontFamily: "Inter-Regular",
                              fontSize: "14px",
                              lineHeight: "1.1",
                            }}
                          >
                            {item?.username}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            style={{
                              fontFamily: "Inter-Regular",
                              fontSize: "14px",
                              lineHeight: "1.1",
                            }}
                          >
                            {item?.firstName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            style={{
                              fontFamily: "Inter-Regular",
                              fontSize: "14px",
                              lineHeight: "1.1",
                            }}
                          >
                            {item?.lastName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            style={{
                              fontFamily: "Inter-Regular",
                              fontSize: "14px",
                              lineHeight: "1.1",
                              textTransform: "capitalize",
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                            }}
                          >
                            <GoDotFill
                              size={18}
                              color={
                                item?.status === "ACTIVE"
                                  ? "#46b035"
                                  : "#d90429"
                              }
                            />
                            {item?.status?.toLowerCase()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            style={{
                              fontFamily: "Inter-Regular",
                              fontSize: "14px",
                              lineHeight: "1.1",
                            }}
                          >
                            {item?.dateCreated
                              ? moment(item?.dateCreated).format(
                                  "DD.MM.YYYY HH:mm"
                                )
                              : ""}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ textAlign: "right" }}>
                          <IconButton
                            style={{ padding: 0 }}
                            onClick={() => navigate(`/users/edit/${item.id}`)}
                          >
                            <CiEdit color={theme.palette.primary.main} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              )}
              <TableFooter
                style={{
                  position: "sticky",
                  bottom: 0,
                  background: "white",
                }}
              >
                <TableRow>
                  <TablePagination
                    style={{ borderBottom: "none" }}
                    rowsPerPageOptions={[5, 10, 25]}
                    colSpan={8}
                    count={data?.totalElements ?? 0}
                    rowsPerPage={size ? size : data?.size}
                    page={data?.number ?? page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <Outlet />
    </PageLayout>
  );
}
